import React, { useState, useEffect } from 'react';
import { fetchAuthSession } from 'aws-amplify/auth';
import Breadcrumb from '../Breadcrumb';
import DateTimeRangePicker from '../DateTimeRangePicker';

interface LogsProps {
  projectId: string;
}

const Logs: React.FC<LogsProps> = ({ projectId }) => {
  const [pipelineLogs, setPipelineLogs] = useState<any[]>([]);
  const [costLogs, setCostLogs] = useState<any[]>([]);
  const [activeTab, setActiveTab] = useState<'pipeline' | 'cost'>('pipeline');
  const [loading, setLoading] = useState(true);
  const [searchText, setSearchText] = useState('');
  const [expandedRows, setExpandedRows] = useState<Set<number>>(new Set());
  const [columnWidths, setColumnWidths] = useState<number[]>([50, 120, 180, 120, 200, 200, 200, 200, 200]);
  const [resizing, setResizing] = useState<{ columnIndex: number; startX: number; startWidth: number } | null>(null);
  const [startDate, setStartDate] = useState(() => {
    const date = new Date();
    date.setDate(date.getDate() - 7);
    date.setHours(0, 0, 0, 0);
    return date;
  });
  const [endDate, setEndDate] = useState(() => new Date());


  useEffect(() => {
    fetchLogs();
  }, [projectId, startDate, endDate]);



  const getCacheKey = () => `logs_${projectId}`;

  const getCachedLogs = () => {
    try {
      const cached = localStorage.getItem(getCacheKey());
      return cached ? JSON.parse(cached) : { ranges: [], pipeline_logs: [], cost_logs: [] };
    } catch {
      return { ranges: [], pipeline_logs: [], cost_logs: [] };
    }
  };

  const setCachedLogs = (cache: any) => {
    try {
      localStorage.setItem(getCacheKey(), JSON.stringify(cache));
    } catch {}
  };

  const fetchLogs = async () => {
    const requestStart = startDate?.getTime() || 0;
    const requestEnd = endDate?.getTime() || Date.now();
    const cache = getCachedLogs();
    
    // Find overlapping ranges and gaps
    const overlaps = cache.ranges.filter((r: any) => 
      r.start <= requestEnd && r.end >= requestStart
    );
    
    let gaps = [{ start: requestStart, end: requestEnd }];
    overlaps.forEach((overlap: any) => {
      gaps = gaps.flatMap(gap => {
        if (gap.end < overlap.start || gap.start > overlap.end) return [gap];
        const parts = [];
        if (gap.start < overlap.start) parts.push({ start: gap.start, end: overlap.start - 1 });
        if (gap.end > overlap.end) parts.push({ start: overlap.end + 1, end: gap.end });
        return parts;
      });
    });

    try {
      const session = await fetchAuthSession();
      const token = session.tokens?.idToken?.toString();
      
      // Fetch missing ranges
      for (const gap of gaps) {
        const params = new URLSearchParams();
        params.append('startTime', gap.start.toString());
        params.append('endTime', gap.end.toString());
        
        const response = await fetch(`${process.env.REACT_APP_API_URL}/projects/${projectId}/logs?${params}`, {
          headers: {
            'Authorization': token || '',
            'Content-Type': 'application/json'
          }
        });
        
        if (response.ok) {
          const data = await response.json();
          cache.pipeline_logs.push(...(data.pipeline_logs || []));
          cache.cost_logs.push(...(data.cost_logs || []));
          cache.ranges.push({ start: gap.start, end: gap.end });
        }
      }
      
      // Merge overlapping ranges
      cache.ranges.sort((a: any, b: any) => a.start - b.start);
      const merged = [];
      for (const range of cache.ranges) {
        if (merged.length && merged[merged.length - 1].end >= range.start - 1) {
          merged[merged.length - 1].end = Math.max(merged[merged.length - 1].end, range.end);
        } else {
          merged.push(range);
        }
      }
      cache.ranges = merged;
      
      setCachedLogs(cache);
      
      // Filter logs for current request
      const filterLogs = (logs: any[]) => logs.filter((log: any) => 
        log.timestamp >= requestStart && log.timestamp <= requestEnd
      );
      
      setPipelineLogs(filterLogs(cache.pipeline_logs));
      setCostLogs(filterLogs(cache.cost_logs));
      
    } catch (error) {
      console.error('Error fetching logs:', error);
    }
    setLoading(false);
  };

  const formatTimestamp = (timestamp: number) => {
    return new Date(timestamp).toLocaleString('en-GB', {
      day: '2-digit',
      month: '2-digit', 
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false
    });
  };

  const toggleRow = (index: number) => {
    const newExpanded = new Set(expandedRows);
    if (newExpanded.has(index)) {
      newExpanded.delete(index);
    } else {
      newExpanded.add(index);
    }
    setExpandedRows(newExpanded);
  };

  const toggleAllRows = () => {
    const currentLogs = activeTab === 'pipeline' ? pipelineLogs : costLogs;
    const filtered = currentLogs.filter(log => {
      const logDate = new Date(log.timestamp);
      const start = startDate;
      const end = endDate;
      
      if (start && logDate < start) return false;
      if (end && logDate > end) return false;
      
      if (!searchText) return true;
      
      const searchableText = JSON.stringify(log.message).toLowerCase();
      return searchableText.includes(searchText.toLowerCase());
    });
    
    if (expandedRows.size === filtered.length) {
      setExpandedRows(new Set());
    } else {
      setExpandedRows(new Set(filtered.map((_, index) => index)));
    }
  };

  const highlightText = (text: string, search: string) => {
    if (!search) return text;
    const parts = text.split(new RegExp(`(${search})`, 'gi'));
    return parts.map((part, i) => 
      part.toLowerCase() === search.toLowerCase() ? 
        <span key={i} style={{backgroundColor: 'yellow'}}>{part}</span> : part
    );
  };

  const truncateText = (text: string, maxLength: number = 100) => {
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
  };

  const handleMouseDown = (e: React.MouseEvent, columnIndex: number) => {
    e.preventDefault();
    setResizing({
      columnIndex,
      startX: e.clientX,
      startWidth: columnWidths[columnIndex]
    });
  };

  const handleMouseMove = (e: MouseEvent) => {
    if (!resizing) return;
    const diff = e.clientX - resizing.startX;
    const newWidth = Math.max(50, resizing.startWidth + diff);
    const newWidths = [...columnWidths];
    newWidths[resizing.columnIndex] = newWidth;
    setColumnWidths(newWidths);
  };

  const handleMouseUp = () => {
    setResizing(null);
  };

  React.useEffect(() => {
    if (resizing) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [resizing]);

  const JsonCell = ({ data, isExpanded, searchText }: { data: any, isExpanded: boolean, searchText: string }) => {
    const jsonString = JSON.stringify(data, null, 4);
    return (
      <div className="json-cell">
        {isExpanded ? 
          <pre>{highlightText(jsonString, searchText)}</pre> :
          <span>{truncateText(JSON.stringify(data))}</span>
        }
        <button 
          className="copy-btn"
          onClick={() => copyToClipboard(jsonString)}
          title="Copy to clipboard"
        >
          📋
        </button>
      </div>
    );
  };

  const currentLogs = activeTab === 'pipeline' ? pipelineLogs : costLogs;
  const filteredLogs = currentLogs.filter(log => {
    const logDate = new Date(log.timestamp);
    const start = startDate;
    const end = endDate;
    
    if (start && logDate < start) return false;
    if (end && logDate > end) return false;
    
    if (!searchText) return true;
    
    const searchableText = JSON.stringify(log.message).toLowerCase();
    return searchableText.includes(searchText.toLowerCase());
  });

  if (loading) {
    return <div className="loading">Loading logs...</div>;
  }

  return (
    <div className="module-content">
      <Breadcrumb items={[
        { label: 'Home', path: '/' },
        { label: 'Projects', path: '/' },
        { label: projectId, path: `/project/${projectId}` },
        { label: 'Logs' }
      ]} />
      
      <div className="logs-header">
        <h2>Logs</h2>
        <button onClick={() => {
          localStorage.removeItem(getCacheKey());
          fetchLogs();
        }} className="refresh-btn">
          Refresh
        </button>
      </div>

      <div className="logs-filters">
        <input
          type="text"
          placeholder="Search logs..."
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          className="search-input"
        />
        <DateTimeRangePicker
          startDate={startDate}
          endDate={endDate}
          onDateRangeChange={(start, end) => {
            setStartDate(start);
            setEndDate(end);
          }}
        />
      </div>

      <div className="tabs">
        <button 
          className={`tab ${activeTab === 'pipeline' ? 'active' : ''}`}
          onClick={() => setActiveTab('pipeline')}
        >
          Pipeline Logs ({pipelineLogs.length})
        </button>
        <button 
          className={`tab ${activeTab === 'cost' ? 'active' : ''}`}
          onClick={() => setActiveTab('cost')}
        >
          Cost Logs ({costLogs.length})
        </button>
      </div>

      <div className="logs-table">
        <table>
          <thead>
            <tr>
              {(activeTab === 'pipeline' 
                ? ['', 'Run Type', 'Timestamp', 'Run ID', 'Input', 'Output', 'Context', 'Pipeline Config', 'Other Attributes']
                : ['', 'Run Type', 'Timestamp', 'Run ID', 'Usage', 'Other Attributes']
              ).map((header, index) => (
                <th key={index} style={{ width: columnWidths[index], position: 'relative' }}>
                  {index === 0 ? (
                    <button 
                      onClick={toggleAllRows}
                      className="expand-all-btn"
                      title={expandedRows.size === filteredLogs.length ? 'Collapse all' : 'Expand all'}
                    >
                      {expandedRows.size === filteredLogs.length ? '▼' : '▶'}
                    </button>
                  ) : header}
                  {index < columnWidths.length - 1 && (
                    <div 
                      className="resize-handle"
                      onMouseDown={(e) => handleMouseDown(e, index)}
                    />
                  )}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filteredLogs.length === 0 ? (
              <tr><td colSpan={activeTab === 'pipeline' ? 9 : 6} className="empty-state">No logs found</td></tr>
            ) : (
              filteredLogs.map((log: any, index: number) => {
                const { run_type, run_id, input, output, context, pipeline_config, usage, project_id, timestamp, ...otherAttributes } = log.message;
                const isExpanded = expandedRows.has(index);
                return (
                  <tr key={index}>
                    <td style={{ width: columnWidths[0] }}>
                      <button 
                        onClick={() => toggleRow(index)}
                        className="expand-btn"
                      >
                        {isExpanded ? '▼' : '▶'}
                      </button>
                    </td>
                    <td style={{ width: columnWidths[1] }}>
                      <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', display: 'block' }}>
                        {highlightText(run_type || '', searchText)}
                      </span>
                    </td>
                    <td style={{ width: columnWidths[2] }}>
                      <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', display: 'block' }}>
                        {formatTimestamp(log.timestamp)}
                      </span>
                    </td>
                    <td style={{ width: columnWidths[3] }}>
                      <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', display: 'block' }}>
                        {highlightText(run_id || '', searchText)}
                      </span>
                    </td>
                    {activeTab === 'pipeline' ? (
                      <>
                        <td style={{ width: columnWidths[4] }}>
                          <JsonCell data={input} isExpanded={isExpanded} searchText={searchText} />
                        </td>
                        <td style={{ width: columnWidths[5] }}>
                          <JsonCell data={output} isExpanded={isExpanded} searchText={searchText} />
                        </td>
                        <td style={{ width: columnWidths[6] }}>
                          <JsonCell data={context} isExpanded={isExpanded} searchText={searchText} />
                        </td>
                        <td style={{ width: columnWidths[7] }}>
                          <JsonCell data={pipeline_config} isExpanded={isExpanded} searchText={searchText} />
                        </td>
                        <td style={{ width: columnWidths[8] }}>
                          {Object.keys(otherAttributes).length > 0 ? (
                            <JsonCell data={otherAttributes} isExpanded={isExpanded} searchText={searchText} />
                          ) : ''}
                        </td>
                      </>
                    ) : (
                      <>
                        <td style={{ width: columnWidths[4] }}>
                          <JsonCell data={usage} isExpanded={isExpanded} searchText={searchText} />
                        </td>
                        <td style={{ width: columnWidths[5] }}>
                          {Object.keys(otherAttributes).length > 0 ? (
                            <JsonCell data={otherAttributes} isExpanded={isExpanded} searchText={searchText} />
                          ) : ''}
                        </td>
                      </>
                    )}
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      <style>{`
        .logs-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 1rem;
        }
        
        .refresh-btn {
          background: #007bff;
          color: white;
          border: none;
          padding: 0.5rem 1rem;
          border-radius: 4px;
          cursor: pointer;
        }
        
        .logs-filters {
          display: flex;
          gap: 1rem;
          margin-bottom: 1rem;
          align-items: center;
        }
        
        .search-input {
          flex: 1;
          padding: 0.5rem;
          border: 1px solid #ddd;
          border-radius: 4px;
        }
        

        
        .logs-table {
          width: calc(100vw - 250px - 4rem);
          overflow-x: auto;
        }
        
        table {
          width: 100%;
          min-width: 100%;
          border-collapse: collapse;
          font-size: 0.85rem;
          background: white;
        }
        
        th, td {
          border: none;
          padding: 1rem 0.75rem;
          text-align: left;
          vertical-align: top;
        }
        
        th {
          background: #f8f9fa;
          color: #333;
          font-weight: 600;
          font-size: 0.9rem;
          position: sticky;
          top: 0;
          z-index: 5;
        }
        
        tbody tr {
          transition: background-color 0.2s ease;
        }
        
        tbody tr:hover {
          background: #e9ecef !important;
        }
        
        tbody tr:nth-child(odd) {
          background: #f8f9fa;
        }
        
        tbody tr:nth-child(even) {
          background: #ffffff;
        }
        
        pre {
          margin: 0;
          white-space: pre-wrap;
          word-break: break-word;
          max-width: 400px;
          font-size: 0.75rem;
          background: #f8f9fa;
          padding: 0.75rem;
          border-radius: 6px;
          border: 1px solid #e9ecef;
          font-family: 'Monaco', 'Menlo', 'Consolas', monospace;
        }
        
        td span {
          color: #495057;
          font-size: 0.8rem;
          display: block;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }
        
        .empty-state {
          text-align: center;
          color: #666;
          padding: 2rem;
        }
        
        .expand-btn {
          background: #f8f9fa;
          border: 1px solid #ddd;
          border-radius: 6px;
          width: 28px;
          height: 28px;
          cursor: pointer;
          color: #333;
          font-size: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: all 0.2s ease;
        }
        
        .expand-btn:hover {
          background: #e9ecef;
          transform: scale(1.1);
        }
        
        .json-cell {
          position: relative;
        }
        
        .copy-btn {
          position: absolute;
          top: 4px;
          right: 4px;
          background: #f8f9fa;
          border: 1px solid #ddd;
          border-radius: 4px;
          width: 24px;
          height: 24px;
          cursor: pointer;
          font-size: 12px;
          display: none;
          align-items: center;
          justify-content: center;
          opacity: 0.8;
        }
        
        .json-cell:hover .copy-btn {
          display: flex;
        }
        
        .copy-btn:hover {
          background: #e9ecef;
          opacity: 1;
        }
        
        .resize-handle {
          position: absolute;
          top: 0;
          right: 0;
          width: 4px;
          height: 100%;
          cursor: col-resize;
          background: transparent;
        }
        
        .resize-handle:hover {
          background: #ddd;
        }
        
        table {
          table-layout: fixed;
        }
        
        .expand-all-btn {
          background: #f8f9fa;
          border: 1px solid #ddd;
          border-radius: 6px;
          width: 28px;
          height: 28px;
          cursor: pointer;
          color: #333;
          font-size: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: all 0.2s ease;
        }
        
        .expand-all-btn:hover {
          background: #e9ecef;
          transform: scale(1.1);
        }
        
        .tabs {
          display: flex;
          border-bottom: 1px solid #ddd;
          margin-bottom: 1rem;
        }
        
        .tab {
          background: none;
          border: none;
          padding: 0.75rem 1.5rem;
          cursor: pointer;
          border-bottom: 2px solid transparent;
          transition: all 0.2s ease;
          font-weight: 500;
        }
        
        .tab:hover {
          background: #f8f9fa;
        }
        
        .tab.active {
          border-bottom-color: #007bff;
          color: #007bff;
        }
      `}</style>
    </div>
  );
};

export default Logs;